import { ref, readonly } from 'vue'

export type Theme = 'light' | 'dark'

/*
  Single shared theme source. The initial value is resolved by the inline
  script in index.html before first paint — this composable only reads what
  that script decided, so the two never disagree and there is no flash.
*/
const isDark = ref(
  typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark'
)

let listening = false

function apply(theme: Theme, persist = true) {
  isDark.value = theme === 'dark'
  const root = document.documentElement
  if (theme === 'dark') root.setAttribute('data-theme', 'dark')
  else root.removeAttribute('data-theme')
  if (persist) {
    try {
      localStorage.setItem('theme', theme)
    } catch {
      /* private mode — the in-memory value still works for this session */
    }
  }
}

export function useTheme() {
  // Follow the OS until the visitor states a preference of their own.
  if (!listening && typeof window !== 'undefined') {
    listening = true
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      let stored: string | null = null
      try {
        stored = localStorage.getItem('theme')
      } catch {
        /* ignore */
      }
      if (!stored) apply(e.matches ? 'dark' : 'light', false)
    })
  }

  return {
    isDark: readonly(isDark),
    theme: () => (isDark.value ? 'dark' : 'light') as Theme,
    toggle: () => apply(isDark.value ? 'light' : 'dark'),
    set: (t: Theme) => apply(t),
  }
}
